import React, { useState, useEffect, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import FormHelperText from "@material-ui/core/FormHelperText";
import Typography from "@material-ui/core/Typography";
import MenuItem from "@material-ui/core/MenuItem";
import { makeStyles } from "@material-ui/core";

import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Autocomplete, {
	createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  maxWidth: {
    width: "100%",
  },
}));

const filterOptions = createFilterOptions({
	trim: true,
});

// Como o canal aparece ao lado do nome da conexao: duas conexoes do mesmo
// numero (uma direta na Meta, outra pela Gupshup) so se distinguem por isso.
const CONNECTION_TYPE_LABELS = {
	waba: "API oficial (Meta)",
	gupshup: "API oficial (Gupshup)",
	baileys: "QR Code",
	baileys_official: "QR Code",
	whaileys: "QR Code",
	webchat: "WebChat",
	messenger: "Messenger",
	instagram: "Instagram"
};

const connectionTypeLabel = type =>
	CONNECTION_TYPE_LABELS[String(type || "").toLowerCase()] || "";

// Ordem alfabetica como se le em portugues: sem separar maiusculas de
// minusculas nem jogar os nomes acentuados ("Íris") para o fim.
const sortByName = list =>
	[...(list || [])].sort((a, b) =>
		String(a?.name || "").localeCompare(String(b?.name || ""), "pt-BR", {
			sensitivity: "base"
		})
	);

const TransferTicketModal = ({ modalOpen, onClose, ticketid, ticketWhatsappId }) => {
	const history = useHistory();
	const [options, setOptions] = useState([]);
	const [queues, setQueues] = useState([]);
	const [allQueues, setAllQueues] = useState([]);
	const [whatsapps, setWhatsapps] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [selectedUser, setSelectedUser] = useState(null);
	const [selectedQueue, setSelectedQueue] = useState('');
	const [selectedWhatsapp, setSelectedWhatsapp] = useState(ticketWhatsappId);
	// Estado proprio do envio, separado do "loading" da busca de atendentes: a
	// busca termina sozinha e liberava o botao com a transferencia ainda no ar.
	// O ref trava no mesmo instante do clique - state do React so vale no render
	// seguinte, e Enter repetido dispara varios submits antes disso.
	const [submitting, setSubmitting] = useState(false);
	const submittingRef = useRef(false);
	const classes = useStyles();

	const { user: loggedInUser } = useContext(AuthContext);
	const isAdmin = loggedInUser?.profile === "admin";
	const isAdminLikeUser = isAdmin || Boolean(loggedInUser?.supervisorMode);

	// Depois de um envio bem-sucedido o ref continua travado de proposito - a
	// tecla ainda pode estar repetindo quando a resposta chega. Quem destrava e
	// a reabertura do modal, que so acontece por uma nova acao do atendente.
	useEffect(() => {
		if (modalOpen) {
			submittingRef.current = false;
			setSubmitting(false);
		}
	}, [modalOpen]);

	// A cada abertura a conexao volta a ser a do proprio chamado. Antes a escolha
	// ficava de uma abertura para a outra, e o chamado podia mudar de conexao sem
	// ninguem ter mexido no campo desta vez.
	useEffect(() => {
		if (modalOpen) {
			setSelectedWhatsapp(ticketWhatsappId);
		}
	}, [modalOpen, ticketWhatsappId]);

	// So conta como troca quando o campo aponta para OUTRA conexao. E so entao a
	// conexao vai no pedido: transferir de atendente ou de fila nao mexe nela.
	const connectionChanged =
		isAdminLikeUser &&
		Boolean(selectedWhatsapp) &&
		Number(selectedWhatsapp) !== Number(ticketWhatsappId);
	const currentConnectionName =
		whatsapps.find(item => Number(item.id) === Number(ticketWhatsappId))?.name || "";
	const targetConnectionName =
		whatsapps.find(item => Number(item.id) === Number(selectedWhatsapp))?.name || "";

	useEffect(() => {
		if (!modalOpen) {
			return;
		}

		setLoading(true);
		const delayDebounceFn = setTimeout(() => {
			const fetchUsers = async () => {
				try {
					const { data } = await api.get(`/tickets/${ticketid}/transfer-options`, {
						params: { searchParam },
					});
					setOptions(sortByName(data.users));
					setAllQueues(data.queues || []);
					setQueues(data.queues || []);
					setWhatsapps(data.whatsapps || []);
					setLoading(false);
				} catch (err) {
					setLoading(false);
					toastError(err);
				}
			};

			fetchUsers();
		}, 300);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, modalOpen, ticketid]);

	const handleClose = () => {
		onClose();
		setSearchParam("");
		setSelectedUser(null);
		setOptions([]);
		setQueues(allQueues);
		setWhatsapps(whatsapps);
		setSelectedQueue("");
	};

	const handleSaveTicket = async e => {
		e.preventDefault();
		if (!ticketid) return;
		if (submittingRef.current) return;
		submittingRef.current = true;
		setSubmitting(true);
		try {
			let data = {};

			if (selectedUser) {
				data.userId = selectedUser.id
			}

			if (selectedQueue && selectedQueue !== null) {
				data.queueId = selectedQueue

				if (!selectedUser) {
					data.status = 'pending';
					data.userId = null;
				}
			}

			if (connectionChanged) {
				data.whatsappId = selectedWhatsapp;
			}

			if (!selectedUser && !selectedQueue && !connectionChanged) {
				submittingRef.current = false;
				setSubmitting(false);
				handleClose();
				return;
			}

			await api.put(`/tickets/${ticketid}/transfer`, data);

			setSubmitting(false);
			history.push(`/tickets`);
		} catch (err) {
			submittingRef.current = false;
			setSubmitting(false);
			toastError(err);
		}
	};

	return (
		<Dialog open={modalOpen} onClose={handleClose} maxWidth="lg" scroll="paper">
			<form onSubmit={handleSaveTicket}>
				<DialogTitle id="form-dialog-title">
					{i18n.t("transferTicketModal.title")}
				</DialogTitle>
				<DialogContent dividers>
					<Autocomplete
						style={{ width: 300, marginBottom: 20 }}
						getOptionLabel={option =>
							typeof option === "string" ? option : `${option.name}`
						}
						getOptionSelected={(option, value) =>
							typeof value === "string" ? false : option.id === value.id
						}
						onChange={(e, newValue) => {
							setSelectedUser(newValue);
							setQueues(allQueues);
							if (newValue == null) {
								setSelectedQueue('');
							}
						}}
						options={options}
						filterOptions={filterOptions}
						autoHighlight
						openOnFocus
						noOptionsText={i18n.t("transferTicketModal.noOptions")}
						loading={loading}
						renderInput={params => (
							<TextField
								{...params}
								label={i18n.t("transferTicketModal.fieldLabel")}
								variant="outlined"
								autoFocus
								onChange={e => setSearchParam(e.target.value)}
								helperText="Opcional. Preencha apenas se quiser definir um responsável."
								InputProps={{
									...params.InputProps,
									endAdornment: (
										<React.Fragment>
											{loading ? (
												<CircularProgress color="inherit" size={20} />
											) : null}
											{params.InputProps.endAdornment}
										</React.Fragment>
									),
								}}
							/>
						)}
					/>
					{(isAdminLikeUser || queues.length > 0) && (
					<FormControl variant="outlined" className={classes.maxWidth}>
						<InputLabel>{i18n.t("transferTicketModal.fieldQueueLabel")}</InputLabel>
						<Select
							value={selectedQueue}
							onChange={(e) => setSelectedQueue(e.target.value)}
							label={i18n.t("transferTicketModal.fieldQueuePlaceholder")}
						>
							<MenuItem value={''}>&nbsp;</MenuItem>
							{queues.map((queue) => (
								<MenuItem key={queue.id} value={queue.id}>{queue.name}</MenuItem>
							))}
						</Select>
					</FormControl>
					)}
					{isAdminLikeUser && (
							<FormControl variant="outlined" className={classes.maxWidth} style={{ marginTop: 20 }}>
								<InputLabel>{i18n.t("transferTicketModal.fieldConnectionLabel")}</InputLabel>
								<Select
									value={selectedWhatsapp}
									onChange={(e) => setSelectedWhatsapp(e.target.value)}
									label={i18n.t("transferTicketModal.fieldConnectionPlaceholder")}
								>
									{whatsapps.map((whasapp) => (
										<MenuItem key={whasapp.id} value={whasapp.id}>
											{whasapp.name}
											{connectionTypeLabel(whasapp.connectionType)
												? ` · ${connectionTypeLabel(whasapp.connectionType)}`
												: ""}
										</MenuItem>
									))}
								</Select>
								<FormHelperText>
									{i18n.t("transferTicketModal.connectionHelp")}
								</FormHelperText>
							</FormControl>
					)}
					{connectionChanged && (
						<Typography
							variant="body2"
							color="error"
							className={classes.maxWidth}
							style={{ marginTop: 12 }}
							data-connection-warning
						>
							{i18n.t("transferTicketModal.connectionChangeWarning", {
								from: currentConnectionName || "-",
								to: targetConnectionName || "-"
							})}
						</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleClose}
						color="secondary"
						disabled={loading || submitting}
						variant="outlined"
					>
						{i18n.t("transferTicketModal.buttons.cancel")}
					</Button>
					<ButtonWithSpinner
						variant="contained"
						type="submit"
						color="primary"
						loading={loading || submitting}
					>
						{i18n.t("transferTicketModal.buttons.ok")}
					</ButtonWithSpinner>
				</DialogActions>
			</form>
		</Dialog>
	);
};

export default TransferTicketModal;
